import React from "react";
import { MapPin, Settings, Star } from "lucide-react";
import { HERO, HERO_STATS, CLIENTS, KEY_STATS } from "../mock/mock";

export default function Hero({ onRecruit, onCandidater }) {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="hero"
      className="relative overflow-hidden bg-[#0c1a2c] pt-36 pb-20"
    >
      {/* subtle radial glow */}
      <div className="pointer-events-none absolute -top-40 right-0 h-[500px] w-[500px] rounded-full bg-[#F5941E]/10 blur-[120px]" />
      <div className="pointer-events-none absolute top-1/3 -left-40 h-[400px] w-[400px] rounded-full bg-blue-500/5 blur-[120px]" />
      {/* grid texture */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6">
        <div className="max-w-4xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#F5941E]/40 bg-[#F5941E]/10 px-4 py-2 text-xs font-bold tracking-wider text-[#F5941E] animate-[fadeUp_0.6s_ease]">
            <span className="h-2 w-2 rounded-full bg-[#F5941E] animate-pulse" />
            {HERO.badge}
          </div>

          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-[3.6rem] font-extrabold leading-[1.05] text-white tracking-tight animate-[fadeUp_0.7s_ease]">
            {HERO.title}
          </h1>

          <p className="mt-6 max-w-2xl text-base sm:text-lg leading-relaxed text-slate-300 animate-[fadeUp_0.8s_ease]">
            {HERO.description}
          </p>

          <div className="mt-8 flex items-center gap-2 text-sm text-slate-400">
            <MapPin size={16} className="text-[#F5941E]" />
            {HERO.location}
          </div>

          {/* Highlight card */}
          <div className="mt-6 max-w-2xl rounded-2xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur-sm transition-all duration-300 hover:border-[#F5941E]/30 hover:bg-white/[0.05]">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#F5941E]/15 text-[#F5941E]">
                <Settings size={22} />
              </div>
              <div>
                <h3 className="font-semibold text-white">
                  {HERO.card.title}
                </h3>
                <p className="mt-1 text-sm text-slate-400">
                  {HERO.card.subtitle}
                </p>
              </div>
            </div>
          </div>

          {/* CTAs */}
          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <button
              onClick={onRecruit}
              className="inline-flex items-center justify-center rounded-full bg-[#F5941E] hover:bg-[#e8850e] px-8 py-4 font-semibold text-white shadow-lg shadow-orange-900/20 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl"
            >
              Recruter maintenant
            </button>
            <button
              onClick={() => scrollTo("jobs")}
              className="inline-flex items-center justify-center rounded-full border border-white/15 bg-white/5 px-8 py-4 font-semibold text-white transition-all duration-300 hover:bg-white/10 hover:-translate-y-0.5"
            >
              Je cherche un poste
            </button>
          </div>

          {/* Mini stats */}
          <div className="mt-10 flex flex-wrap items-center gap-8">
            {HERO_STATS.map((s) => (
              <div key={s.label} className="flex items-center gap-3">
                {s.stars && (
                  <div className="flex text-[#F5941E]">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={16} fill="currentColor" />
                    ))}
                  </div>
                )}
                <div>
                  <div className="text-lg font-bold text-white">{s.value}</div>
                  <div className="text-xs text-slate-400">{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Trust bar */}
      <div className="relative mt-20 border-t border-white/10 pt-12">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-center text-xs font-bold tracking-[0.2em] text-slate-500">
            ILS NOUS FONT CONFIANCE
          </p>
          <div className="group relative mt-8 overflow-hidden marquee-mask">
            <div className="flex w-max animate-marquee items-center group-hover:[animation-play-state:paused]">
              {[...CLIENTS, ...CLIENTS].map((c, i) => (
                <span
                  key={`${c}-${i}`}
                  className="mx-8 sm:mx-12 shrink-0 text-lg sm:text-xl font-extrabold tracking-wide text-slate-500 grayscale transition-all duration-300 hover:text-slate-200"
                >
                  {c}
                </span>
              ))}
            </div>
          </div>

          <div className="mt-12 grid grid-cols-2 gap-6 border-t border-white/10 pt-10 max-w-md mx-auto">
            {KEY_STATS.map((s) => (
              <div key={s.label} className="text-center">
                <div className="text-3xl sm:text-4xl font-extrabold text-[#F5941E]">
                  {s.value}
                </div>
                <div className="mt-1 text-sm text-slate-400">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
